import { Dispatch } from 'redux';
import axios, { CancelTokenSource } from 'axios';

import { QUERY_STATE } from '@tmapy/beetlejuice-utils';
import { ThunkAction } from '@tmapy/redux';

import type { AttachmentCreateRequest } from '../../openapi/types';
import { docsRetrieve } from '../../openapi/operations/docsRetrieve';
import { docsDestroy } from '../../openapi/operations/docsDestroy';
import { attachmentsCreate } from '../../openapi/operations/attachmentsCreate';
import { callApi } from '../../utils/api';

import * as con from '../constants';

export const loadDoc =
  (docId: number): ThunkAction =>
  async (dispatch) => {
    dispatch({
      type: con.DOCDETAIL_LOAD_WAITING,
    });
    const respond = await docsRetrieve({ query: { id: docId } });
    if (respond.data) {
      dispatch({
        type: con.DOCDETAIL_LOAD_SUCCESS,
        data: respond.data,
      });
    } else {
      dispatch({
        type: con.DOCDETAIL_LOAD_FAILURE,
      });
    }
  };

export const removeDoc =
  (docId: number, redirect: Function): ThunkAction =>
  async (dispatch) => {
    dispatch({
      type: con.DOCDETAIL_REMOVE_WAITING,
    });
    const respond = await docsDestroy({ query: { id: docId } });
    if (respond.ok) {
      redirect();
      dispatch({
        type: con.DOCDETAIL_REMOVE_SUCCESS,
      });
    } else {
      dispatch({
        type: con.DOCDETAIL_REMOVE_FAILURE,
      });
    }
  };

export const updateDoc = (data: any) =>
  ({
    type: con.DOCDETAIL_UPDATE,
    data,
  }) as const;

export const uploadFile = async (
  dispatch: Dispatch,
  file: FormDataEntryValue,
  docUrl: string,
  attempt = 0,
  defaultAttachmentType: con.ATTACHMENT_TYPES,
) => {
  const cancelToken = axios.CancelToken.source();
  const formData = new FormData() as FormData & AttachmentCreateRequest;
  formData.set('file', file);
  formData.set('attachment_type', defaultAttachmentType);
  formData.set('doc', docUrl);

  const respond = await attachmentsCreate({
    request: formData,
    onUploadProgress: ({ loaded, total }) =>
      dispatch({
        type: con.DOCDETAIL_FILES_SET_UPLOAD_PROGRESS,
        progress: loaded / total,
        cancelToken,
        file,
      }),
    cancelToken,
  });
  if (respond.data) {
    dispatch({
      type: con.DOCDETAIL_FILES_UPLOAD_SUCCESS,
      data: respond.data,
      file,
    });
  } else if (respond.status === 500 && attempt < 5) {
    // Pgpool Load Balancing - timeout and repeat upload = max 5x
    const timeout = 1000 * attempt;
    setTimeout(() => {
      uploadFile(dispatch, file, docUrl, attempt + 1, defaultAttachmentType);
    }, timeout);
  } else {
    dispatch({
      type: con.DOCDETAIL_FILES_UPLOAD_FAILURE,
      file,
      uploadErrors: respond?.error?.data ?? [{ message: '', code: 'unknown_server_error' }],
    });
  }
};

export const uploadFiles =
  (
    input: HTMLInputElement,
    docUrl: string,
    defaultAttachmentType: con.ATTACHMENT_TYPES = con.ATTACHMENT_TYPES.OTHER,
  ): ThunkAction =>
  async (dispatch) => {
    const files = Array.from(input.files!);
    input.value = '';
    dispatch({
      type: con.DOCDETAIL_FILES_UPLOAD_WAITING,
      files,
    });
    files.forEach((file) => uploadFile(dispatch, file, docUrl, 0, defaultAttachmentType));
  };

export const cancelUploadFile = (cancelToken: CancelTokenSource) => {
  cancelToken.cancel('');
  return {
    type: con.DOCDETAIL_FILES_UPLOAD_CANCEL,
    cancelToken,
  };
};

export const deleteFile =
  (fileId: string): ThunkAction =>
  async (dispatch) => {
    dispatch({
      type: con.DOCDETAIL_FILES_DELETE_START,
      payload: {
        fileId,
        deleteQueryState: QUERY_STATE.WAITING,
      },
    });
    const response = await callApi(`${fileId}`, 'DELETE');
    if (response.ok) {
      dispatch({
        type: con.DOCDETAIL_FILES_DELETE_SUCCESS,
        fileId,
      });
    }
  };

export const setAttachmentType =
  (name: string, value: string): ThunkAction =>
  async (dispatch, getState) => {
    const state = getState();
    const attachment = {
      ...state.doc.data.attachments.find((att) => att.url === name),
      attachment_type: value,
    };
    const respond = await callApi(`${name}`, 'PUT', attachment);
    if (respond.data) {
      dispatch({
        type: con.DOCDETAIL_FILES_SET_TYPE_SUCCESS,
        name,
        attachment,
      });
    }
  };

export const uploadSignedProtocol =
  (file: FormDataEntryValue, docUrl: string, docId: number): ThunkAction =>
  async (dispatch) => {
    const formData = new FormData();
    formData.set('handover_protocol', file);

    dispatch({
      type: con.DOCDETAIL_SIGNED_PROTOCOL_UPLOAD_WAITING,
    });

    const respond = await callApi(`${docUrl}upload_signed_protocol/`, 'PATCH', formData);
    if (respond.ok) {
      dispatch({
        type: con.DOCDETAIL_SIGNED_PROTOCOL_UPLOAD_SUCCESS,
      });
      dispatch(loadDoc(docId));
    } else {
      dispatch({
        type: con.DOCDETAIL_SIGNED_PROTOCOL_UPLOAD_FAILURE,
      });
    }
  };
